import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { ROLE_LABEL, type ClientRole } from '../access/roles.ts';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading, PageHeader } from '../components/States.tsx';
import { loadClientOrgs, loadProjects, previewAudit, type OrgOption } from './api.ts';

export interface PreviewTarget {
  readonly clientOrgId: string;
  readonly clientOrgName: string;
  readonly role: ClientRole;
}

export interface PreviewScreenProps {
  readonly sb: SupabaseClient;
  readonly tenantId: string;
  readonly onStart: (t: PreviewTarget) => void;
}

/**
 * Preview as client (MB decision 9): a tenant admin or manager picks a client organisation and sees exactly its view,
 * read-only. Starting is audited as the staff member (portal_preview_audit), and refused by the database for anyone
 * else, so the preview never opens without its audit row.
 */
export function PreviewScreen({ sb, tenantId, onStart }: PreviewScreenProps): ReactElement {
  const [orgs, setOrgs] = useState<OrgOption[] | null>(null);
  // how many live projects each organisation is linked to (R99: a preview of an organisation with none is empty)
  const [linked, setLinked] = useState<ReadonlyMap<string, number> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [org, setOrg] = useState('');
  const [role, setRole] = useState<ClientRole>('client_viewer');
  const [busy, setBusy] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const load = (): void => {
    setError(null);
    loadClientOrgs(sb, tenantId)
      .then((o) => {
        setOrgs(o);
        setOrg((now) => now || o[0]?.id || '');
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
    loadProjects(sb, { kind: 'tenant', tenantId })
      .then((ps) => {
        const n = new Map<string, number>();
        for (const p of ps) {
          if (p.clientOrgId && p.status !== 'archived') n.set(p.clientOrgId, (n.get(p.clientOrgId) ?? 0) + 1);
        }
        setLinked(n);
      })
      .catch(() => setLinked(null));
  };

  useEffect(load, [sb, tenantId]);

  async function start(e: FormEvent): Promise<void> {
    e.preventDefault();
    const chosen = orgs?.find((o) => o.id === org);
    if (!chosen) return;
    setBusy(true);
    setStartError(null);
    try {
      await previewAudit(sb, chosen.id, 'started', { role });
      onStart({ clientOrgId: chosen.id, clientOrgName: chosen.name, role });
    } catch (err: unknown) {
      setStartError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page page--narrow">
      <PageHeader
        title="Preview as client"
        subtitle="See the client portal exactly as a client organisation sees it. Nothing can be changed in a preview."
      />
      {error && <ErrorState message={error} onRetry={load} />}
      {!orgs && !error && <Loading what="client organisations" />}
      {orgs && orgs.length === 0 && (
        <div className="panel">
          <p className="body">There are no client organisations yet. Add one in Clients first.</p>
        </div>
      )}
      {orgs && orgs.length > 0 && (
        <form className="panel stack" onSubmit={(e) => void start(e)} aria-label="Preview as client">
          <label className="label muted" htmlFor="preview-org">
            Client organisation
          </label>
          <select id="preview-org" className="input" value={org} onChange={(e) => setOrg(e.target.value)}>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          {linked && org && !linked.get(org) && (
            <p className="caption" role="note">
              No project is linked to {orgs.find((o) => o.id === org)?.name ?? 'this organisation'}, so its
              portal shows nothing, whatever is published or shared. Open the project in Projects and choose
              the organisation under &ldquo;Client organisation&rdquo; first.
            </p>
          )}
          <label className="label muted" htmlFor="preview-role">
            See it as
          </label>
          <select
            id="preview-role"
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as ClientRole)}
          >
            <option value="client_viewer">{ROLE_LABEL.client_viewer}</option>
            <option value="client_admin">{ROLE_LABEL.client_admin}</option>
          </select>
          <p className="caption muted">
            The preview is written to the audit log in your name, with the organisation and every report you
            open.
          </p>
          {startError && (
            <p className="field__error caption" role="alert">
              {startError}
            </p>
          )}
          <div className="row">
            <Button type="submit" variant="primary" busy={busy}>
              {busy ? 'Starting preview…' : 'Start preview'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
